import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, interval, ReplaySubject } from 'rxjs';
import { throttle, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import { PresentationSettings } from 'messages/websocket.messages';

import SoundControl from '../../sound-control';
import { socialAppSelectors } from '../../state/app';
import { UserId } from '../../types';
import { SceneService, VisiblePlayerObject } from '../scene.service';

import { sortSubscriptions } from './subscription.helper';
import {
  AudibleUser,
  DistanceUser,
  PLAYERS_IN_VIEW_INTERVAL,
  SubscriberState,
  SubscriptionOptions,
  SUBSCRIPTION_LIMIT,
  GroupUser,
  VISIBLE_SUBSCRIPTIONS_LIMIT,
} from './subscription.types';

export interface BasicUser {
  userId: string;
  firstName: string;
  lastName: string;
}

/*

The Subscription Service handles the subscription logic for all of the video and audio streams in the application
When a user joins a room a peer connection is created. See video-room.service.ts and room-participant.ts
We also create a peer connection for cross room participants during a cross room presentation. See cross-room-participant.service.ts
Those streams are then added to an observable. For example the participantVideoStream$ and participantAudioStream$
found in the video room service. The streams can then be piped to a video element where we display the video.

Once the peer connection is created we must determine wether the users audio and video should be required
for other participants in the application. For ex: if the user is presenting there video and audio should always be required.
This is different however from their video and audio being muted. A user video can be muted but still required.

There are 12 reasons a user should subscribe to another participant or video stream in the application
0.AUDIBLE,
1.VISIBLE,
2.GROUP,
3.GROUP_SHARE,
4.AUDIENCE,
5.GALLERY,
6.SPEAKING,
7.SCREEN_SHARE,
8.MIRROR,
9.PRESENTER,
10.MIX,
11.LIVESTREAM,

Based on those reasons and the current state of the application we sort the subscriptions by calling sortSubscriptions()
which is a helper function found in subscription.helper.ts
Once we have determined for each subscription type if the video or audio should be required
we then call removeDuplicateIds() which returns only one instance of the participant based on the subscription reason.
*/

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService extends Loggable {
  private presenters: UserId[] = [];
  private audience: UserId[] = [];
  private gallery: UserId[] = [];
  private speaking: UserId[] = [];
  private visible: DistanceUser[] = [];
  private group: GroupUser[] = [];
  private audible: AudibleUser[] = [];
  private groupShares: UserId[] = [];

  private userId?: string;
  private isUserPresenting = false;
  private presentationSettings?: PresentationSettings;
  private isPresentationAudioMixed = false;
  private isPresentationActive = false;
  private isToastActive = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private unrestrictedZoom = false;
  private speakingParticipantsV1 = false;
  private isUserInGroupShare = false;

  private executeTransition = false;

  public subscription$ = new BehaviorSubject<SubscriberState[]>([]);

  private soundControl = SoundControl.getInstance(this.sceneService);

  public constructor(
    private store: Store,
    private sceneService: SceneService,
  ) {
    super();
    this.setDisplayName('SubscriptionService');
  }

  public init(): void {
    this.info('Starting subscription service');

    this.destroyed$ = new ReplaySubject(1);

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_audio_mix)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationAudioMixed) => (this.isPresentationAudioMixed = isPresentationAudioMixed));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_unrestricted_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((unrestrictedZoom) => (this.unrestrictedZoom = unrestrictedZoom));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.speaking_participants_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speakingParticipantsV1) => (this.speakingParticipantsV1 = speakingParticipantsV1));

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => (this.isUserPresenting = isUserPresenting));

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => (this.isPresentationActive = isPresentationActive));

    combineLatest([
      this.store.select(socialAppSelectors.isToastActive),
      // this is a query for an active icebreaker
      this.store.select(socialAppSelectors.isConvoInAnnounce),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isToastActive, isConvoInAnnounce]) => {
        this.isToastActive = isToastActive || isConvoInAnnounce;
        this.handleSubscriptions();
      });

    this.store
      .select(socialAppSelectors.getPresentationSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentationSettings) => (this.presentationSettings = presentationSettings));

    this.store
      .select(socialAppSelectors.getAudience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((audience) => {
        this.audience = audience
          .map((a) => a.userId)
          .filter(notNil)
          .filter((id) => this.userId !== id);
        this.handleSubscriptions();
      });

    this.store
      .select(socialAppSelectors.getGallery)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((gallery) => {
        this.gallery = gallery
          .map((g) => g.userId)
          .filter(notNil)
          .filter((id) => this.userId !== id);
        this.handleSubscriptions();
      });

    this.store
      .select(socialAppSelectors.getLocalEventPresenters)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presenters) => {
        this.presenters = presenters.filter((id) => this.userId !== id && !id.includes('livestream'));
        this.handleSubscriptions();
      });

    combineLatest([
      this.store.select(socialAppSelectors.getPresentation),
      this.store.select(socialAppSelectors.getActiveSpeakers),
      this.store.select(socialAppSelectors.isOpenModeActive),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([presentation, activeSpeaker, isOpenModeActive]) => {
        const presentationSpeaking = Object.keys(presentation?.speakingParticipants ?? {});

        if (isOpenModeActive) {
          this.speaking = [...activeSpeaker];
        } else {
          if (presentation) {
            this.executeTransition = true;
          }
          this.speaking = [...presentationSpeaking];
        }

        this.handleSubscriptions();
      });

    this.store
      .select(socialAppSelectors.getParticipantMediaState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((participants) => {
        this.audible = Object.entries(participants)
          .filter(
            ([userId, mediaState]) =>
              userId !== this.userId && !mediaState.audioMuted && notNil(mediaState.volume) && mediaState.volume > 0,
          )
          .map(([userId, mediaState]) => ({ userId, volume: mediaState.volume ?? 0 }));
        this.handleSubscriptions();
      });

    this.sceneService.primaryGroupMembers$
      .pipe(
        throttle(() => interval(PLAYERS_IN_VIEW_INTERVAL), { trailing: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((group) => {
        this.group = group.map(({ userId, visible }) => ({ userId, visible }));
        this.handleSubscriptions();
      });

    this.sceneService.playersInView$
      .pipe(
        throttle(() => interval(PLAYERS_IN_VIEW_INTERVAL), { trailing: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((players: Map<string, VisiblePlayerObject>) => {
        this.visible = [...players]
          .filter(([userId]) => this.userId !== userId)
          .map(([userId, { distance }]) => ({ userId, distance }));
        this.handleSubscriptions();
      });

    combineLatest([
      this.store.select(socialAppSelectors.getMyGroupScreenShare),
      this.store.select(socialAppSelectors.getGroupScreenShareUsers),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([myGroupScreenShare, groupShareParticipants]) => {
        this.isUserInGroupShare = !!myGroupScreenShare;
        // if user has joined a group share, subscribe to my group share participants
        if (this.isUserInGroupShare) {
          const ids: string[] = myGroupScreenShare?.viewerUserIds ?? [];
          const presenterId = myGroupScreenShare?.presenter?.userId ?? '';
          this.groupShares = [...ids, ...[presenterId]];
          this.handleSubscriptions();
        }
        // filter out active group share participants
        else {
          this.groupShares = groupShareParticipants;
          this.handleSubscriptions();
        }
      });
  }

  private handleSubscriptions(): void {
    const {
      audible,
      audience,
      gallery,
      group,
      groupShares,
      isPresentationActive,
      isToastActive,
      isUserInGroupShare,
      isUserPresenting,
      presentationSettings,
      presenters,
      speaking,
      speakingParticipantsV1,
      visible,
    } = this;
    const options: SubscriptionOptions = {
      isUserPresenting,
      isPresentationActive,
      isToastActive,
      speakingParticipantsV1,
      isAudioMixed: this.isPresentationActive && this.isPresentationAudioMixed,
      presentationSettings,
      unrestrictedZoom: this.unrestrictedZoom,
      isUserInGroupShare,
    };

    const subscribers = sortSubscriptions({
      audible,
      audience,
      gallery,
      presenters,
      speaking,
      group,
      visible,
      groupShares,
      options,
    })
      .map((subscription, index) => ({
        ...subscription,
        videoRequired: index <= VISIBLE_SUBSCRIPTIONS_LIMIT ? subscription.videoRequired : false,
      }))
      .slice(0, SUBSCRIPTION_LIMIT);
    this.subscription$.next(subscribers);
  }

  public destroy(): void {
    this.info('Destroying subscription service');
    this.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public clear(): void {
    this.presenters = [];
    this.audible = [];
    this.audience = [];
    this.gallery = [];
    this.speaking = [];
    this.visible = [];
    this.groupShares = [];
    this.group = [];
    this.subscription$.next([]);
  }
}
